import { useState } from 'react'
import { Link } from 'react-router-dom'
import { UserPlus, X, Check } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { useAuthStore } from '@/stores/auth'

export default function GuestAccountPrompt() {
  const { isAuthenticated, register, guestData } = useAuthStore()
  const [showPrompt, setShowPrompt] = useState(!isAuthenticated)
  const [isCreating, setIsCreating] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  if (!showPrompt || isAuthenticated) return null

  const handleCreateAccount = async () => {
    setError('')

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsCreating(true)

    try {
      await register({
        name: guestData?.name || 'Guest User',
        email: guestData?.email || '',
        phone: guestData?.phone || '',
        password,
      })
      setShowPrompt(false)
    } catch (err) {
      setError('Failed to create account. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <UserPlus className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">
              Create an Account to Save Your Preferences
            </h3>
            <p className="text-sm text-muted-foreground">
              Track orders, save addresses, and checkout faster next time
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowPrompt(false)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Password
            </label>
            <Input
              type="password"
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Confirm Password
            </label>
            <Input
              type="password"
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-3">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreateAccount}
            disabled={isCreating}
            className="flex-1 sm:flex-none"
          >
            {isCreating ? 'Creating...' : 'Create Account'}
          </Button>
          <span className="text-sm text-muted-foreground">or</span>
          <Link to="/login">
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>
            By creating an account, you agree to BotTola's Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  )
}
